import { ChangeDetectorRef, Directive, ElementRef, HostBinding, HostListener, Input, NgZone, OnDestroy } from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';

import { MessageBusChannels, MessageBusService } from '../../services/message-bus.service';
import { tooltipId } from './tooltip.component';

@Directive({
  selector: '[appTooltip]',
  standalone: false,
})
export class TooltipDirective implements OnDestroy {
  @HostBinding('attr.tabIndex') tabIndex: string = '0';
  private _describedBy: string = null;
  public set describedBy(value: string) {
    this._describedBy = value;
    if (value === null) {
      this.elementRef.nativeElement.removeAttribute('aria-describedby');
    } else {
      this.elementRef.nativeElement.setAttribute('aria-describedby', tooltipId);
    }
  }
  public get describedBy(): string {
    return this._describedBy;
  }

  private tooltipValue: string;
  private scrollSubscription: Subscription | null = null;

  @Input() public set appTooltip(text: string) {
    this.tooltipValue = text;
  }

  constructor(
    private messageBusService: MessageBusService,
    private cd: ChangeDetectorRef,
    private elementRef: ElementRef,
    private ngZone: NgZone,
  ) {}

  @HostListener('focus', ['$event'])
  @HostListener('mouseenter', ['$event'])
  public onActive(event: MouseEvent | KeyboardEvent): void {
    this.describedBy = tooltipId;
    this.messageBusService.to(MessageBusChannels.tooltipShow, { text: this.tooltipValue, event: event });
    this.cd.detectChanges();
    this.startScrollTracking();
  }

  @HostListener('focusout')
  @HostListener('mouseleave')
  @HostListener('document:keydown.escape')
  public onDeactive(): void {
    this.stopScrollTracking();
    this.describedBy = null;
    this.messageBusService.to(MessageBusChannels.tooltipHide, '');
    this.cd.detectChanges();
  }

  private startScrollTracking(): void {
    if (this.scrollSubscription !== null) {
      return;
    }
    this.ngZone.runOutsideAngular(() => {
      this.scrollSubscription = fromEvent(document, 'scroll', { capture: true }).subscribe(() => this.onScroll());
    });
  }

  private stopScrollTracking(): void {
    if (this.scrollSubscription === null) {
      return;
    }
    this.scrollSubscription.unsubscribe();
    this.scrollSubscription = null;
  }

  private onScroll(): void {
    if (this.describedBy === null) {
      return;
    }
    if (!this.isInViewport(this.elementRef.nativeElement)) {
      this.ngZone.run(() => this.onDeactive());
      return;
    }
    this.ngZone.run(() =>
      this.messageBusService.to(MessageBusChannels.tooltipReposition, { target: this.elementRef.nativeElement }),
    );
  }

  private isInViewport(element: HTMLElement): boolean {
    const rect: DOMRect = element.getBoundingClientRect();
    const viewportWidth: number = document.documentElement.clientWidth || window.innerWidth;
    const viewportHeight: number = document.documentElement.clientHeight || window.innerHeight;
    return rect.bottom > 0 && rect.top < viewportHeight && rect.right > 0 && rect.left < viewportWidth;
  }

  public ngOnDestroy(): void {
    this.stopScrollTracking();
  }
}
